import {IDBPDatabase, IDBPObjectStore, openDB, StoreNames} from 'idb';


import {Board} from '../models/types';
import {KanbanDBSchema} from "./KanbanDBSchema";
import {SimpleIDBDatabase} from "../models/__tests__/SimpleIDBDatabase";
import {createDefaultBoard} from "./createDefaultBoard";
import {StoreName} from "./StoreName";

// Операция синхронизации
export interface SyncOperation {
	id: string;
	entityType: 'board' | 'column' | 'task' | 'comment';
	entityId: string;
	operation: 'create' | 'update' | 'delete';
	data: any;
	priority: number; // 1-высокий, 2-средний, 3-низкий
	attempts: number;
	maxAttempts: number;
	error?: string;
	createdAt: Date;
	updatedAt: Date;
}

// Метаданные приложения
export interface Metadata {
	key: string;
	value: any;
	updatedAt: Date;
}

// Конфигурация базы данных
const DB_CONFIG = {
	name: 'sitask-db',
	version: 1,
	stores: {
		boards: 'id, title, updatedAt',
		columns: 'id, boardId, order',
		tasks: 'id, localId, serverId, columnId, boardId, isSynced, updatedAt',
		syncQueue: 'id, entityType, entityId, priority, createdAt',
		metadata: 'key',
	},
	indexes: {
		boards: [
			{name: 'by-title', keyPath: 'title'},
			{name: 'by-updated', keyPath: 'updatedAt'},
		],
		columns: [
			{name: 'by-board', keyPath: 'boardId'},
			{name: 'by-order', keyPath: 'order'},
		],
		tasks: [
			{name: 'by-localId', keyPath: 'localId', unique: true},

			{name: 'by-column', keyPath: 'columnId'},
			{name: 'by-board', keyPath: 'boardId'},
			{name: 'by-sync', keyPath: ['isSynced', 'updatedAt']},
			{name: 'by-updated', keyPath: 'updatedAt'},
		],
		syncQueue: [
			{name: 'by-priority', keyPath: ['priority', 'createdAt']},
			{name: 'by-entity', keyPath: ['entityType', 'entityId']},
		],
	},
} as const;

type IdbpObjectStore = IDBPObjectStore<KanbanDBSchema, any, any, "versionchange">;

// Инициализация базы данных
class Database {
	private db: IDBPDatabase<KanbanDBSchema> | SimpleIDBDatabase | null = null;
	private static instance: Database;

	private constructor() {
	}

	static getInstance(): Database {
		if (!Database.instance) {
			Database.instance = new Database();
		}
		return Database.instance;
	}

	deleteDatabase(){
		indexedDB.deleteDatabase(DB_CONFIG.name);
	}

	/** set instance in tests*/
	setDb(db: IDBPDatabase<KanbanDBSchema>) {
		this.db = db as IDBPDatabase<KanbanDBSchema>;
	}

	createDatabase() {
		this.deleteDatabase();
		this.db = null;
		this.init(false);
	}
	async init(isFillDefault:boolean = true): Promise<IDBPDatabase<KanbanDBSchema>> {
		if (this.db) {
			return this.db as IDBPDatabase<KanbanDBSchema>;
		}
		const self =this;

		function createIndex(boardStore1: IDBPObjectStore<KanbanDBSchema, ArrayLike<StoreNames<KanbanDBSchema>>,
				any, "versionchange">, indexName :any, arg1: any, arg2:any|undefined=undefined) {
			//@ts-ignore
			boardStore1.createIndex(indexName, arg1, arg2);
		}

		const blockedUpgradeBlockingTerminated = {
			upgrade(db: IDBPDatabase<KanbanDBSchema>, oldVersion: number, newVersion: number|null, transaction: IDBTransaction) {
				// Создание хранилищ при первой инициализации
				if (oldVersion >= 1) {
					return;
				}
				const boardStore = db.createObjectStore(StoreName.Boards as any, {keyPath: 'id'});
				createIndex(boardStore, 'by-title', 'title');
				createIndex(boardStore, 'by-updated'  as any, 'updatedAt');
				const columnStore = db.createObjectStore(StoreName.Columns as any, {keyPath: 'id'});
				createIndex(columnStore,'by-board'  as any, 'boardId');
				createIndex(columnStore,'by-order'  as any, 'order');
				const taskStore = db.createObjectStore(StoreName.Tasks as any, {keyPath: 'id'});
				createIndex(taskStore,'by-column'  as any, 'columnId');
				createIndex(taskStore,'by-board'  as any, 'boardId');
				createIndex(taskStore,'by-localId'  as any, 'localId', {unique: true});
				createIndex(taskStore,'by-sync'  as any, ['isSynced', 'updatedAt']);
				createIndex(taskStore,'by-updated'  as any, 'updatedAt');
				const syncStore = db.createObjectStore(StoreName.SyncQueue as any, {keyPath: 'id'});
				createIndex(syncStore,'by-priority'  as any, ['priority', 'createdAt']);
				createIndex(syncStore,'by-entity' as any, ['entityType', 'entityId']);
				db.createObjectStore(StoreName.Metadata as any, {keyPath: 'key'});
				if (isFillDefault) {
					console.log("Initialized default database");
					fillDefaultContent(boardStore as any, columnStore as any, taskStore as any);
				}
			},
		};
		this.db = await openDB<KanbanDBSchema>(DB_CONFIG.name, DB_CONFIG.version, blockedUpgradeBlockingTerminated as any);

		return this.db;
	}

	getDB(): IDBPDatabase<KanbanDBSchema> {
		if (!this.db) {
			throw new Error('Database not initialized. Call init() first.');
		}
		return this.db as IDBPDatabase<KanbanDBSchema>;
	}

	async close(): Promise<void> {
		if (this.db) {
			this.db.close();
			this.db = null;
		}
	}

}

export function fillDefaultContent(
	boardStore: IdbpObjectStore,
	columnStore: IdbpObjectStore,
	taskStore: IdbpObjectStore,
	testBoard:Board|undefined = undefined
) {

	if  (!testBoard){
		testBoard = createDefaultBoard();
	}
	boardStore.put(testBoard);

	testBoard.columns.forEach(col => columnStore.put(col));
	testBoard.tasks.forEach(task => taskStore.put(task));
}


export default Database;
